import React from 'react';
import { Drawer, DrawerBody } from '@fluentui/react-components';
import { Dismiss20Regular, Edit12Regular } from '@fluentui/react-icons';
import styles from '../TaskManagement/css/index.module.css';
import { CustomAvatar } from '../TaskManagement';
import { useSelector, useDispatch } from 'react-redux';
import { GetLessonComments, PostLessonComment, PatchLessonComment } from '../../redux/lessonSlice';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { format } from 'date-fns';
import SafeHTML from '../SafeHTML';

const LessonDetailsSidebar = ({ open, onClose, lesson }) => {
    const dispatch = useDispatch();
    const { lessonComments, buttonLoading } = useSelector(state => state.lesson || { lessonComments: [], buttonLoading: false });
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team || { projectUsersAll: [] });

    const [comment, setComment] = React.useState('');
    const quillRef = React.useRef(null);
    const [editingCommentId, setEditingCommentId] = React.useState(null);
    const [editingCommentText, setEditingCommentText] = React.useState('');
    const editQuillRef = React.useRef(null);

    React.useEffect(() => {
        if (lesson?.id) {
            const projectId = localStorage.getItem('project_id');
            if (projectId) {
                dispatch(GetLessonComments({ lessonId: lesson.id, projectId }));
            }
        }
    }, [lesson, dispatch]);

    const handleSubmit = () => {
        if (!comment.trim() || !lesson?.id) return;
        const body = {
            lesson: lesson.id,
            comment,
        };
        dispatch(PostLessonComment(body));
        setComment('');
        if (quillRef.current) {
            quillRef.current.getEditor().setText('');
        }
    };

    const handleEditComment = item => {
        setEditingCommentId(item.id);
        setEditingCommentText(item.comment);
    };

    const handleSaveEdit = () => {
        if (!editingCommentText.trim() || !editingCommentId) return;
        dispatch(
            PatchLessonComment({
                commentId: editingCommentId,
                body: { comment: editingCommentText },
            }),
        );
        setEditingCommentId(null);
        setEditingCommentText('');
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditingCommentText('');
    };

    const getUserName = id => {
        const found = projectUsersAll?.find(u => u?.value?.user_id === id);
        if (found?.value?.full_name) return found.value.full_name;
        if (user?.id === id) return user.full_name;
        return '';
    };

    return (
        <Drawer
            type="overlay"
            position="end"
            open={open}
            onOpenChange={(e, data) => {
                if (!data.open) onClose();
            }}
            size="medium">
            <DrawerBody>
                <div className="mt-4 d-flex justify-content-between">
                    <div style={{ maxWidth: '80%' }}>
                        <span style={{ color: 'var(--Neutral-Gray-6, #989BAD)', fontSize: '11px' }}>{lesson?.tag || ''}</span>
                        <p className={styles.sidebarTaskText}>{lesson?.issue || ''}</p>
                    </div>
                    <div className="ms-2">
                        <Dismiss20Regular role="button" color="var(--Neutral-Gray-6, #989BAD)" onClick={onClose} />
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2 mt-2">
                    <div>
                        <CustomAvatar name={lesson?.created_by_name} size={20} />
                    </div>
                    <span className={styles.sidebarWsTagLabel + ' mb-0'}>
                        {lesson?.created ? new Date(lesson.created).toLocaleDateString() : '-- --'}
                    </span>
                </div>

                <div className={styles.divider} />

                <p className={styles.sidebarWsTagLabel}>Comments</p>

                {lessonComments?.length > 0 && (
                    <div className="mt-3">
                        {lessonComments.map(item => (
                            <div key={item.id} className="mb-4">
                                {editingCommentId === item.id ? (
                                    <div>
                                        <ReactQuill
                                            ref={editQuillRef}
                                            placeholder="Edit comment"
                                            modules={{
                                                toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                                            }}
                                            theme="snow"
                                            className="mt-1"
                                            defaultValue={editingCommentText}
                                            onChange={t => {
                                                if (t === '<p><br></p>') setEditingCommentText('');
                                                else setEditingCommentText(t);
                                            }}
                                        />
                                        <div className="mt-2 d-flex gap-2">
                                            <button
                                                onClick={handleSaveEdit}
                                                disabled={!editingCommentText.trim() || buttonLoading}
                                                type="button"
                                                className="btn btn-primary btn-sm">
                                                Save
                                            </button>
                                            <button onClick={handleCancelEdit} type="button" className="btn btn-secondary btn-sm">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <SafeHTML 
                                            html={item.comment} 
                                            className={styles.commentText}
                                            tag="p"
                                        />
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className={styles.sidebarWsTagLabel + ' mb-0 mt-1'}>
                                                {getUserName(item.created_by)} {item.created && format(new Date(item.created), 'dd/MM/yyyy')}
                                            </div>
                                            {item.created_by === user?.id && (
                                                <div>
                                                    <Edit12Regular cursor="pointer" onClick={() => handleEditComment(item)} color="var(--Brand-Secondary)" />
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-2">
                    <ReactQuill
                        ref={quillRef}
                        placeholder="Add a comment"
                        modules={{
                            toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                        }}
                        theme="snow"
                        className="mt-1"
                        defaultValue={comment}
                        onChange={t => {
                            if (t === '<p><br></p>') setComment('');
                            else setComment(t);
                        }}
                    />
                </div>
                {comment && (
                    <div className="mt-4 d-flex justify-content-between align-items-center">
                        <button onClick={handleSubmit} disabled={!comment || buttonLoading} type="button" className="btn btn-primary btn-sm">
                            Add Comment {buttonLoading && <span className="spinner-border spinner-border-sm ms-1" />}
                        </button>
                    </div>
                )}
            </DrawerBody>
        </Drawer>
    );
};

export default LessonDetailsSidebar;


